import React, { useContext } from 'react';

import { media } from 'context';
import { Animate, Clickable, Grid, Icon, Layout, Text } from 'ds';
import { IconName } from 'shared';
import { actions } from 'store/ServicesRequest';
import { selectServicesRequestServices } from 'store/ServicesRequest/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import StepContainer from './StepContainer';
import StepTitle from './StepTitle';
import { generateServicesRequestStepPath } from './utils';

interface Props {}

const SERVICES: { iconName: IconName; label: string }[] = [
  {
    iconName: 'featherDuster',
    label: 'Cleaning'
  },
  {
    iconName: 'beverageSupplies',
    label: 'Pantry restocking'
  },
  {
    iconName: 'chair',
    label: 'Furniture purchase'
  },
  {
    iconName: 'ruler',
    label: 'Layout design'
  },
  {
    iconName: 'wifi',
    label: 'Internet & IT'
  },
  {
    iconName: 'kitchen',
    label: 'Catering'
  },
  {
    iconName: 'wrench',
    label: 'Vendor sourcing & management'
  }
] as const;

const ServicesRequestServices: React.FC<Props> = () => {
  const services = useAppSelector(selectServicesRequestServices);
  const dispatch = useAppDispatch();
  const { xs, sm } = useContext(media);
  const isMobile = xs || sm;

  return (
    <StepContainer
      step="services"
      nextHref={generateServicesRequestStepPath({ step: 'location' })}
      ctaDisabled={!services?.length}
      stepCompletedEventValue={services || []}
      stepName="services"
      mobileStyle="compact"
      {...(isMobile ? { textLayoutProps: { paddingY: 36 } } : {})}
    >
      <StepTitle>Which office services are you interested in?</StepTitle>
      <Grid
        {...(isMobile
          ? { itemsPerRow: 1, gap: 16, paddingX: 24, paddingTop: 24, paddingBottom: 134 }
          : { itemsPerRow: 3, gap: 15 })}
      >
        {SERVICES.map(({ iconName, label }, index) => {
          const isSelected = services?.includes(label);

          return (
            <Animate duration={300} delay={(index + 1) * 30 + 50}>
              <Clickable
                data-cy={`service-option-${label.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, 'and')}`}
                fullWidth
                onClick={() => {
                  dispatch(
                    actions.setServices(
                      isSelected ? services?.filter(s => s !== label) || [] : [...(services || []), label]
                    )
                  );
                }}
              >
                <Layout
                  borderRadius={16}
                  {...(isSelected
                    ? { color: 'blue-50', borderColor: 'blue-500', borderWidth: 2, padding: 19 }
                    : { color: 'white', borderColor: 'gray-50', borderWidth: 1, padding: 20 })}
                  {...(isMobile
                    ? { height: 72, align: 'center', justify: 'space-between', direction: 'row-reverse' }
                    : { height: 136, justify: 'flex-end', direction: 'column' })}
                  gap={8}
                >
                  <Icon name={iconName} size={28} />
                  <Text size={isMobile ? 'body-md' : 'body-sm'} align="left">
                    {label}
                  </Text>
                </Layout>
              </Clickable>
            </Animate>
          );
        })}
      </Grid>
    </StepContainer>
  );
};

export default ServicesRequestServices;
